import type { RouteObject } from 'react-router';

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  category: 'common' | 'professional' | 'mvp';
  order: number;
}

export interface AgentManifest {
  agentId: string;
  routes: RouteObject[];
  /**
   * 외부공유 수신자 뷰어 라우트 (F-04 §7). 회원 인증 없이 공유 JWT(GUEST)로 접근하는
   * 공개 영역에 배선된다. 경로·가드·쿠키는 공통(shell)이 처리하므로, 에이전트는
   * 뷰어 컴포넌트만 등록한다. 미사용 에이전트는 생략(옵셔널).
   */
  shareRoutes?: RouteObject[];
  menuItems: MenuItem[];
  permissions: string[];
}

const VALID_PERMISSIONS = ['SUPER', 'ADMIN', 'OWNER', 'STAFF', 'MEMBER', 'VIEWER', 'GUEST'] as const;

export function validateRegistry(registry: AgentManifest[]): void {
  const agentIds = registry.map((a) => a.agentId);
  const uniqueIds = new Set(agentIds);
  if (uniqueIds.size !== agentIds.length) throw new Error('[Registry] agentId 중복');

  const allPaths = registry.flatMap((a) => [...a.routes, ...(a.shareRoutes ?? [])].map((r) => r.path));
  const uniquePaths = new Set(allPaths);
  if (uniquePaths.size !== allPaths.length) throw new Error('[Registry] route path 중복');

  for (const agent of registry) {
    for (const item of agent.menuItems) {
      if (item.id !== agent.agentId) throw new Error(`[Registry] menuItem.id(${item.id}) ≠ agentId(${agent.agentId})`);
    }
    for (const perm of agent.permissions) {
      if (!VALID_PERMISSIONS.includes(perm as (typeof VALID_PERMISSIONS)[number])) {
        throw new Error(`[Registry] 유효하지 않은 permission: ${perm}`);
      }
    }
    // 라우트 정의 표준 (D-02 §4.1.6): Component 참조형 강제, element 금지 (routes + shareRoutes)
    for (const route of [...agent.routes, ...(agent.shareRoutes ?? [])]) {
      const r = route as { path?: string; Component?: unknown; element?: unknown };
      if ('element' in route && r.element !== undefined) {
        throw new Error(`[Registry] route(${r.path ?? '?'})에 element 사용 금지 — Component 참조형 사용 (D-02 §4.1.6)`);
      }
      if (r.Component === undefined) {
        throw new Error(`[Registry] route(${r.path ?? '?'})에 Component 누락 — Component 참조형 필수 (D-02 §4.1.6)`);
      }
    }
  }
}
